<template>
  <div class="relative">
    <ValidationObserver
      ref="inputObserver" >
      <div
        v-for="(button, index) in buttons"
        :key="button.id"
        class="list-item-wrapper" >
        <ValidationProvider
          :ref="`inputProvider-${index}`"
          v-slot="{ errors, failedRules }"
          class="relative"
          :name="schema.title"
          :rules="validators">
          <div
            :ref="`input-${index}`"
            class="sb-editable sb2-bordered sb2-hoverable sb2-focusable"
            :placeholder="schema.placeholder"
            contenteditable="plaintext-only"
            @input="inputChange($event, index)"
            @keydown.enter="enterPressed($event, index)"
            @keyup.delete="deleteItem(index)"
            @keydown.delete="markForDelete(index)"
            @keydown.up="move(index - 1)"
            @keydown.down="move(index + 1)"
            @blur="removeEmpty(index)" />
          <SBIcon
            v-if="!button.readOnly && buttons.filter(b => b.value).length > 1"
            class="delete-item"
            :icon="SBIconList.CLOSE_THIN"
            :size="10"
            :hover-size="20"
            :color="SBColors.greyPrimary"
            :background-color="SBColors.white"
            :hover-icon-color="SBColors.blackPrimary"
            @click="removeOption(index, true)" />
          <div
            v-if="schema.subtitle && !errors[0]"
            class="subtitle flex flex-align-center">
            <svg
              v-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string' && schema.subtitlePrefixIcon.endsWith('.svg')"
              class="mr-1 flex-start-image"
              :data-src="schema.subtitlePrefixIcon"
              fill="currentColor"
              width="10"
              height="10" />
            <img
              v-else-if="schema.subtitlePrefixIcon && typeof schema.subtitlePrefixIcon === 'string'"
              :src="schema.subtitlePrefixIcon"
              style="width: 10px; height: 10px;"
              class="mr-1 flex-start-image"
            >
            <SBIcon
              v-else-if="!schema.noSubtitlePrefixIcon"
              class="mr-1"
              :icon="SBIconList.INFO"
              :color="SBColors.greyLight"
              :size="10" />
            <span>{{ schema.subtitle }}</span>
          </div>
          <div
            v-if="errors[0]"
            class="error flex flex-align-center mb-2"
            :class="{ warning: warningValidators.includes(Object.keys(failedRules)[0])}">
            <SBIcon
              class="mr-1"
              :icon="warningValidators.includes(Object.keys(failedRules)[0]) ? SBIconList.INFO : SBIconList.ERROR"
              :color="warningValidators.includes(Object.keys(failedRules)[0]) ? '#DEBD30' : SBColors.redPrimary"
              :size="10" />
            <span>{{ schema.validatorMessages && schema.validatorMessages[Object.keys(failedRules)[0]] ? schema.validatorMessages[Object.keys(failedRules)[0]] : errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div
        v-if="showAddButton"
        class="add-more-btn"
        :class="{
          disabled: !valid,
          black: schema.blackButtons,
        }"
        @click="addNewInput">
        <span class="flex flex-align-center">
          <svg
            v-if="schema.addButtonIcon && typeof schema.addButtonIcon === 'string' && schema.addButtonIcon.endsWith('.svg')"
            class="mr-1 flex-start-image"
            :data-src="schema.addButtonIcon"
            fill="currentColor"
            width="8"
            height="8" />
          <img
            v-else-if="schema.addButtonIcon && typeof schema.addButtonIcon === 'string'"
            :src="schema.addButtonIcon"
            style="width: 8px; height: 8px;"
            class="mr-2"
          >
          <SBIcon
            v-else-if="!schema.noAddButtonIcon"
            class="mr-2"
            :icon="SBIconList.PLUS"
            :color="schema.blackButtons ? SBColors.blackPrimary : SBColors.bluePrimary"
            :size="8" />
          <span>{{ schema.addButtonText || 'Add' }}</span>
        </span>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import randomString from '@/helpers/randomString';

export default {
  name: 'InputListWrapper',
  components: {
    SBIcon,
  },
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    validators: {
      type: String,
      default: '',
    },
    schema: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      buttons: [],
      valid: true,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    warningValidators() {
      return this.schema.warningValidators ? this.schema.warningValidators.split(',') : [];
    },
    showAddButton() {
      return !this.schema.noAddButton && (!this.schema.maxButtons || (this.buttons.length < this.schema.maxButtons));
    },
  },
  watch: {
    buttons: {
      handler() {
        this.emitData();
      },
      immediate: false,
      deep: true,
    },
  },
  mounted() {
    if (this.schema.value) {
      this.buttons = this.schema.value.map(button => ({
        id: randomString(),
        value: button,
        canBeRemoved: true,
      }));
    }
    if (this.schema.stepID) {
      this.$eventBus.$on(`validate-${this.schema.stepID}`, () => {
        if (!this.$refs.inputObserver) return;
        this.$refs.inputObserver.validate();
      });
    }
    setTimeout(() => {
      // Set initial values
      this.buttons.forEach((button, i) => {
        this.$refs[`input-${i}`][0].innerText = button.value;
        this.$refs[`inputProvider-${i}`][0].syncValue(button.value);
      });
      this.emitData(true);
    }, 0);
  },
  methods: {
    /** Removes an item from the list if it is empty. */
    removeEmpty(index) {
      if (this.buttons.length > 1 && this.buttons[index].value.trim() === '') {
        this.removeOption(index);
      }
    },
    /** Focuses item and puts the cursor to the end. */
    focusItem(index) {
      const targetItem = this.$refs[`input-${index}`][0];
      targetItem.focus();
      setTimeout(() => { // Setting the cursor to the end
        const range = document.createRange();
        range.selectNodeContents(targetItem);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      });
    },
    /** Moves focus to the target item. */
    move(targetItem) {
      if (targetItem >= 0 && targetItem < this.buttons.length) {
        this.focusItem(targetItem);
      }
    },
    /** Method checks if the content is empty and marks the item to be deleted. This prevents the item to be removed immediatelly when the content is cleared so user can type in new content but if delete is pressed one more time the item will be removed. */
    markForDelete(index) {
      this.buttons[index].canBeRemoved = false;

      // The logic below works but we decided it would be too confusing for the user. If we decide to enable this feaure just remove the comment and remove the current first line of the method.

      // if (
      //   (this.buttons.length > 1) ||
      //   (this.buttons[index].value.trim() === '')
      // ) {
      //   this.buttons[index].canBeRemoved = true;
      // } else {
      //   this.buttons[index].canBeRemoved = false;
      // }
    },
    /** Deletes an item when delete key is used. */
    deleteItem(index) {
      if (this.buttons.length > 1 && this.buttons[index].canBeRemoved) {
        this.buttons.splice(index, 1);
        if (index) {
          this.focusItem(index - 1);
        }

        this.$nextTick(() => {
          this.$refs.inputObserver.validate({ silent: true }).then((valid) => {
            this.valid = valid;
          });
        });
      }
    },
    /** Deletes an item when x button is used. */
    removeOption(index) {
      this.buttons.splice(index, 1);
      this.$nextTick(() => {
        this.$refs.inputObserver.validate({ silent: true }).then((valid) => {
          this.valid = valid;
        });
      });
    },
    inputChange(e, i) {
      const trimmed = {
        id: this.buttons[i].id,
        value: this.$refs[`input-${i}`][0].innerText.trim(),
      };
      // Deleting all the content leaves a new line character so we need to remove it manually
      if (this.$refs[`input-${i}`][0].innerText.trim() === '') {
        this.$refs[`input-${i}`][0].innerText = '';
      }
      this.$set(this.buttons, i, trimmed);
      this.$refs[`inputProvider-${i}`][0].syncValue(trimmed.value);
      this.$refs[`inputProvider-${i}`][0]
        .validate()
        .then((valid) => {
          this.valid = valid.valid;
        });
    },
    enterPressed(e, index) {
      if (e.metaKey || e.ctrlKey) {
        this.addNewInput();
      }

      // Code for clearing the new line if needed
      // this.$refs[`input-${index}`][0].innerText = this.$refs[`input-${index}`][0].innerText.trim();
    },
    addNewInput() {
      if (
        !this.valid ||
        this.schema.noAddButton ||
        (this.schema.maxButtons && this.buttons.length >= this.schema.maxButtons)
      ) return;
      this.buttons.push({
        id: randomString(),
        value: '',
        canBeRemoved: true,
      });
      this.$nextTick(() => {
        this.$refs[`inputProvider-${this.buttons.length - 1}`][0].syncValue('');
        this.$refs[`input-${this.buttons.length - 1}`][0].focus();
      });
      this.valid = false;
    },
    /** Checks if the validator errors are all of warning type. */
    onlyWarnings() {
      const fields = Object.values(this.$refs.inputObserver.fields).map(field => Object.keys(field.failedRules)).flat(Infinity);
      const someTest = fields.length ? !fields.some(failed => !this.warningValidators.includes(failed)) : false; // if there are no fields, return false because it means the field is validated silently and no failedRules are being recorded
      return someTest;
    },
    emitData(silent = false) {
      if (!this.$refs.inputObserver) return;
      this.$refs.inputObserver.validate({ silent: true })
        .then((valid) => {
          // this.valid = valid;
          this.$emit('input', {
            valid: this.valid || this.onlyWarnings(),
            name: this.schema.returnKey || '',
            data: {
              value: this.buttons.map(button => button.value),
            },
          });
        })
        .finally(() => {
          // if (reset) this.$refs.inputObserver.reset();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.flex-start-image {
  top: 1px;
  position: relative;
}

.sb-editable.sb2-bordered.sb2-hoverable.sb2-focusable {
  padding: 9px 12px;
  margin-bottom: 8px;
  border-radius: 10px !important;
  // box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
  // border: solid 1px var(--grey-border);
  // background-color: var(--white);
  // min-height: 32px;
  font-size: 14px;
  font-weight: normal;
  color: $blackPrimary;

  &[placeholder]:empty:before {
    content: attr(placeholder);
    color: $greyPrimary;
    cursor: text;
  }
}

.delete-item {
  display: none;
  position: absolute;
  right: 10px;
  top: 7px;
  cursor: pointer;
}

.list-item-wrapper:hover {
  .delete-item {
    display: flex;
  }
}

.add-more-btn {
  // display: inline-block;
  width: fit-content;
  color: $bluePrimary;
  padding: 0 0 0 0;
  // margin-top: 15px;

  &.black {
    color: $blackPrimary;
  }

  span {
    font-weight: 500;
    font-size: 14px;
  }

  &:hover {
    cursor: pointer;
    color: $blackPrimary;
    ::v-deep .sb-icon .mask {
      background-color: $blackPrimary !important;
    }
  }

  &.disabled {
    pointer-events: none;
    color: $greyLight;

    ::v-deep .icon.mask {
      background-color: $greyLight !important;
    }
  }
}
</style>
